import { computed } from "vue";
import type { HeadingProps } from "./heading.types";
import { useSharedState } from "composables/useSharedState";
import { useSlotPresence } from "composables/useSlotPresence";
import { useIcons } from "composables/useIcons";
import { headingClasses } from "./heading.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";

export interface UseHeadingOptions {
  props: Readonly<HeadingProps>;
}

export const useHeading = (options: UseHeadingOptions) => {
  const { props } = options;

  const state = useSharedState(props);

  const { isLeading, isTrailing, leadingIconName, trailingIconName } = useIcons(state);

  const {
    default: hasDefaultSlot,
    leading: hasLeadingSlot,
    trailing: hasTrailingSlot,
  } = useSlotPresence(["default", "leading", "trailing"]);

  const titleText = computed(() => state.title?.value ?? props.title);
  const showLeading = computed(() => isLeading.value || hasLeadingSlot.value);
  const showDefault = computed(() => hasDefaultSlot.value || !!titleText.value);
  const showTrailing = computed(() => isTrailing.value || hasTrailingSlot.value);

  const classes = computed(() =>
    mergeClassProp(
      headingClasses({
        color: state.color?.value ?? props.color,
        size: state.size?.value ?? props.size,
      }),
      props.class,
    ),
  );

  return {
    classes,
    titleText,
    hasDefaultSlot,
    showLeading,
    showDefault,
    showTrailing,
    leadingIconName,
    trailingIconName,
  };
};
